import { useEffect, useState } from "react";

export type NavTone = "light" | "dark";

/**
 * Tracks which `[data-nav-tone]` section sits under the fixed nav band.
 * Uses IntersectionObserver with a root margin clipped to the nav Y range.
 */
export function useNavTone(defaultTone: NavTone = "light"): NavTone {
  const [tone, setTone] = useState<NavTone>(defaultTone);

  useEffect(() => {
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>("[data-nav-tone]")
    );
    if (sections.length === 0) return;

    const ratios = new Map<Element, number>();

    const pickTone = () => {
      let best: { el: Element; ratio: number } | null = null;
      for (const [el, ratio] of ratios) {
        if (ratio <= 0) continue;
        if (!best || ratio > best.ratio) best = { el, ratio };
      }
      if (!best) return;
      const next = best.el.getAttribute("data-nav-tone");
      if (next === "light" || next === "dark") setTone(next);
    };

    // Fixed nav sits at top-5 (~20px) with ~70px height → observe ~0–100px band
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target, entry.intersectionRatio);
        }
        pickTone();
      },
      {
        root: null,
        rootMargin: "0px 0px -90% 0px",
        threshold: [0, 0.05, 0.1, 0.25, 0.5, 0.75, 1],
      }
    );

    for (const section of sections) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return tone;
}
